import React, {
  createContext,
  useReducer,
  useContext,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  DashboardState,
  DashboardAction,
  Dashboard,
  Widget,
  CreateDashboardDTO,
  UpdateDashboardDTO,
  CreateWidgetDTO,
  UpdateWidgetDTO,
  DashboardLayoutItem,
  WidgetDataResult,
  CreateFilterDTO,
  DashboardFilter,
  FilterOptionsResult,
} from '../types';
import { toast } from 'react-toastify';
import i18n from '../../../i18n';
import { DashboardAPI, DashboardReadApi, DraftFilterOverridePayload } from '../api';
import { resolveDateRangeDefault } from '../relativeDatetime';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * For a date_range default that may contain relative expressions like "now-7d",
 * return a concrete `{from, to}` in YYYY-MM-DD form so the date picker can
 * seed without choking. Falls back to the current-year range when anything
 * fails to resolve.
 */
function seedDateRangeValue(defaultValue: any): { from: string; to: string } {
  const year = new Date().getFullYear();
  const yearRange = { from: `${year}-01-01`, to: `${year}-12-31` };
  if (!defaultValue || typeof defaultValue !== 'object') return yearRange;
  const resolved = resolveDateRangeDefault(defaultValue);
  if (
    typeof resolved.from === 'string' && ISO_DATE_PATTERN.test(resolved.from) &&
    typeof resolved.to === 'string' && ISO_DATE_PATTERN.test(resolved.to)
  ) {
    return { from: resolved.from, to: resolved.to };
  }
  return yearRange;
}

const initialState: DashboardState = {
  dashboards: [],
  totalDashboards: 0,
  currentPage: 1,
  pageSize: 10,
  activeDashboard: null,
  draftDashboard: null,
  snapshotDashboard: null,
  hasUnsavedChanges: false,
  isEditing: false,
  isLoading: false,
  isSaving: false,
  error: null,
  selectedWidgetId: null,
  filterValues: {},
  filterOptions: {},
};

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

let tempIdCounter = 0;
function generateTempId(): string {
  return `temp-${Date.now()}-${++tempIdCounter}`;
}

function isTempId(id: string): boolean {
  return id.startsWith('temp-');
}

/**
 * Merge visible filter values with hidden filter draft defaults.
 *
 * In edit mode, hidden filter `defaultValue` changes are held locally and not
 * yet persisted. The backend normally re-injects the saved `default_value`
 * for hidden params, which would overwrite the admin's draft values. Sending
 * the draft hidden values explicitly (with `includeHiddenValues: true`) lets
 * the preview/dashboard reflect the unsaved changes.
 */
function mergeFilterValuesWithHiddenDefaults(
  filterValues: Record<string, any>,
  filters: readonly DashboardFilter[] | undefined,
): Record<string, any> {
  if (!filters || filters.length === 0) return filterValues;
  const merged: Record<string, any> = { ...filterValues };
  filters.forEach((f) => {
    if (!f.hidden) return;
    if (merged[f.parameterName] !== undefined) return;
    if (f.defaultValue != null) {
      merged[f.parameterName] = f.defaultValue;
    }
  });
  return merged;
}

/**
 * Build draft filter overrides from the current draft filter set.
 *
 * The backend uses these to swap query_sql / default_value / config of saved
 * filters during query/options resolution so unsaved edits (cascading SQL,
 * __all__ expansion of hidden allow_all filters, etc.) take effect without
 * persisting the dashboard. Temp (unsaved) filters are skipped — they don't
 * exist on the backend yet, so an override would have nothing to bind to.
 *
 * Filters whose SQL is not loaded (`querySqlLoaded` falsy) are skipped. Filter
 * SQL is lazy-loaded — the dashboard GET omits it; it only arrives via
 * `getFilter` (eager-loaded on edit start, or when the admin opens the filter
 * editor). An unloaded filter has no unsaved edits, so its stored SQL (and its
 * default_value/config, which only change through the same editor that loads the
 * SQL) is already authoritative on the backend; sending an override with the
 * placeholder empty `query_sql` would instead make the backend swap the real
 * stored SQL for an empty string, so the filter resolves to nothing and is
 * effectively not applied — producing different results in the editor than on
 * the view page.
 *
 * Note we gate on `querySqlLoaded`, not on emptiness: a *loaded* filter with an
 * empty `querySql` is a deliberate "clear the SQL, use only the default value"
 * edit, and that empty override must be sent so the preview honours it.
 */
export function buildDraftFilterOverrides(
  filters: readonly DashboardFilter[] | undefined,
): DraftFilterOverridePayload[] | undefined {
  if (!filters || filters.length === 0) return undefined;
  const overrides = filters
    .filter((f) => !isTempId(f.id))
    .filter((f) => f.querySqlLoaded)
    .map((f) => ({
      filter_id: f.id,
      query_sql: f.querySql ?? '',
      default_value: f.defaultValue ?? null,
      config: f.config ?? {},
    }));
  return overrides.length > 0 ? overrides : undefined;
}

function dashboardReducer(
  state: DashboardState,
  action: DashboardAction
): DashboardState {
  switch (action.type) {
    case 'SET_DASHBOARDS':
      return {
        ...state,
        dashboards: action.dashboards,
        totalDashboards: action.total,
        currentPage: action.page,
        pageSize: action.size,
      };
    case 'SET_ACTIVE_DASHBOARD':
      return {
        ...state,
        activeDashboard: action.dashboard,
        selectedWidgetId: null,
      };
    case 'SET_EDITING':
      return { ...state, isEditing: action.isEditing };
    case 'SET_LOADING':
      return { ...state, isLoading: action.isLoading };
    case 'SET_SAVING':
      return { ...state, isSaving: action.isSaving };
    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'START_EDITING':
      return {
        ...state,
        isEditing: true,
        draftDashboard: action.draft,
        snapshotDashboard: action.snapshot,
        hasUnsavedChanges: false,
        selectedWidgetId: null,
      };
    case 'CLEAR_DRAFT':
      return {
        ...state,
        isEditing: false,
        draftDashboard: null,
        snapshotDashboard: null,
        hasUnsavedChanges: false,
        selectedWidgetId: null,
      };
    case 'SET_DRAFT':
      return {
        ...state,
        draftDashboard: action.draft,
        hasUnsavedChanges: true,
      };

    case 'DRAFT_ADD_WIDGET':
      if (!state.draftDashboard) return state;
      return {
        ...state,
        draftDashboard: {
          ...state.draftDashboard,
          widgets: [...state.draftDashboard.widgets, action.widget],
          layout: [...state.draftDashboard.layout, action.layoutItem],
        },
        hasUnsavedChanges: true,
      };
    case 'DRAFT_UPDATE_WIDGET':
      if (!state.draftDashboard) return state;
      return {
        ...state,
        draftDashboard: {
          ...state.draftDashboard,
          widgets: state.draftDashboard.widgets.map((w) =>
            w.id === action.widgetId ? { ...w, ...action.updates } : w
          ),
        },
        hasUnsavedChanges: true,
      };
    case 'DRAFT_REMOVE_WIDGET':
      if (!state.draftDashboard) return state;
      return {
        ...state,
        draftDashboard: {
          ...state.draftDashboard,
          widgets: state.draftDashboard.widgets.filter(
            (w) => w.id !== action.widgetId
          ),
          layout: state.draftDashboard.layout.filter(
            (l) => l.i !== action.widgetId
          ),
        },
        hasUnsavedChanges: true,
        selectedWidgetId:
          state.selectedWidgetId === action.widgetId
            ? null
            : state.selectedWidgetId,
      };
    case 'DRAFT_UPDATE_LAYOUT':
      if (!state.draftDashboard) return state;
      return {
        ...state,
        draftDashboard: {
          ...state.draftDashboard,
          layout: action.layout,
        },
        hasUnsavedChanges: true,
      };
    case 'DRAFT_SET_WIDGET_DATA':
      if (!state.draftDashboard) return state;
      return {
        ...state,
        draftDashboard: {
          ...state.draftDashboard,
          widgets: state.draftDashboard.widgets.map((w) =>
            w.id === action.widgetId ? { ...w, data: action.data } : w
          ),
        },
      };

    case 'SET_WIDGET_DATA':
      if (!state.activeDashboard) return state;
      return {
        ...state,
        activeDashboard: {
          ...state.activeDashboard,
          widgets: state.activeDashboard.widgets.map((w) =>
            w.id === action.widgetId ? { ...w, data: action.data } : w
          ),
        },
      };

    case 'SELECT_WIDGET':
      return { ...state, selectedWidgetId: action.widgetId };
    case 'ADD_DASHBOARD':
      return {
        ...state,
        dashboards: [...state.dashboards, action.dashboard],
      };
    case 'DELETE_DASHBOARD':
      return {
        ...state,
        dashboards: state.dashboards.filter(
          (d) => d.id !== action.dashboardId
        ),
        activeDashboard:
          state.activeDashboard?.id === action.dashboardId
            ? null
            : state.activeDashboard,
      };
    case 'SET_FILTER_VALUE': {
      const newValues = { ...state.filterValues };
      if (action.value === undefined || action.value === null) {
        delete newValues[action.parameterName];
      } else {
        newValues[action.parameterName] = action.value;
      }
      return { ...state, filterValues: newValues };
    }
    case 'SET_FILTER_VALUES':
      return { ...state, filterValues: { ...state.filterValues, ...action.values } };
    case 'SET_FILTER_OPTIONS':
      return { ...state, filterOptions: action.options };
    case 'CLEAR_FILTER_VALUES':
      return { ...state, filterValues: {} };
    case 'DRAFT_ADD_FILTER':
      if (!state.draftDashboard) return state;
      return {
        ...state,
        draftDashboard: {
          ...state.draftDashboard,
          filters: [...state.draftDashboard.filters, action.filter],
        },
        hasUnsavedChanges: true,
      };
    case 'DRAFT_UPDATE_FILTER':
      if (!state.draftDashboard) return state;
      return {
        ...state,
        draftDashboard: {
          ...state.draftDashboard,
          filters: state.draftDashboard.filters.map((f) =>
            f.id === action.filterId ? { ...f, ...action.updates } : f
          ),
        },
        hasUnsavedChanges: true,
      };
    case 'DRAFT_REMOVE_FILTER':
      if (!state.draftDashboard) return state;
      return {
        ...state,
        draftDashboard: {
          ...state.draftDashboard,
          filters: state.draftDashboard.filters.filter(
            (f) => f.id !== action.filterId
          ),
        },
        hasUnsavedChanges: true,
      };
    case 'HYDRATE_FILTER_SQL': {
      // Fill in eager-loaded filter SQL on both draft and snapshot. Filters
      // already loaded (e.g. opened/edited in the filter editor before the
      // eager-load resolved) are left untouched so we never clobber an edit.
      // Hydrating both keeps the save diff consistent and does NOT flip
      // hasUnsavedChanges — this is server data, not a user edit.
      const hydrate = (filters: DashboardFilter[] | undefined) =>
        (filters ?? []).map((f) => {
          if (f.querySqlLoaded) return f;
          const sql = action.sqlByFilterId[f.id];
          if (sql === undefined) return f;
          return { ...f, querySql: sql, querySqlLoaded: true };
        });
      return {
        ...state,
        draftDashboard: state.draftDashboard
          ? { ...state.draftDashboard, filters: hydrate(state.draftDashboard.filters) }
          : state.draftDashboard,
        snapshotDashboard: state.snapshotDashboard
          ? { ...state.snapshotDashboard, filters: hydrate(state.snapshotDashboard.filters) }
          : state.snapshotDashboard,
      };
    }
    default:
      return state;
  }
}

export interface DashboardContextValue extends DashboardState {
  isAdmin: boolean;
  fetchDashboards: (page?: number, size?: number) => Promise<void>;
  selectDashboard: (dashboardId: string, initialFilterValues?: Record<string, any>) => Promise<void>;
  createDashboard: (dto: CreateDashboardDTO) => Promise<Dashboard>;
  updateDashboardMeta: (dashboardId: string, dto: UpdateDashboardDTO) => Promise<void>;
  deleteDashboard: (dashboardId: string) => Promise<void>;
  copyDashboard: (
    sourceDashboardId: string,
    newTitle: string,
    targetWorkspaceId?: number,
  ) => Promise<Dashboard>;
  startEditing: () => void;
  saveDashboard: () => Promise<void>;
  cancelEditing: () => void;
  addWidget: (dto: CreateWidgetDTO) => string;
  updateWidget: (widgetId: string, dto: UpdateWidgetDTO) => void;
  removeWidget: (widgetId: string) => void;
  copyWidget: (widgetId: string) => Promise<void>;
  executeWidgetQuery: (widgetId: string, page?: number, pageSize?: number) => Promise<void>;
  invalidateWidgetCache: (widgetId: string) => Promise<void>;
  updateLayout: (layout: DashboardLayoutItem[]) => void;
  selectWidget: (widgetId: string | null) => void;
  setFilterValue: (parameterName: string, value: any) => void;
  setFilterValues: (values: Record<string, any>) => void;
  clearFilterValues: () => void;
  loadFilterOptions: () => Promise<void>;
  saveDraftFilters: () => Promise<void>;
  reloadEditingDashboard: () => Promise<void>;
  addFilter: (dto: CreateFilterDTO) => void;
  updateFilter: (filterId: string, updates: Partial<DashboardFilter>) => void;
  removeFilter: (filterId: string) => void;
  displayedDashboard: Dashboard | null;
}

export const DashboardContext = createContext<DashboardContextValue>(
  {} as DashboardContextValue
);

export const useDashboard = () => useContext(DashboardContext);

interface DashboardProviderProps {
  children: ReactNode;
  isAdmin?: boolean;
  // read-path overrides (artifact registry serves a dashboard through its own
  // proxies); everything not overridden keeps hitting /custom-dashboards
  api?: Partial<DashboardReadApi>;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
  isAdmin = false,
  api,
}) => {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  const apiRef = useRef<typeof DashboardAPI>({ ...DashboardAPI, ...api });
  apiRef.current = { ...DashboardAPI, ...api };

  const fetchDashboards = useCallback(async (page: number = 1, size: number = 10) => {
    dispatch({ type: 'SET_LOADING', isLoading: true });
    try {
      const result = await apiRef.current.listDashboards(page, size);
      dispatch({
        type: 'SET_DASHBOARDS',
        dashboards: result.dashboards,
        total: result.total,
        page: result.page,
        size: result.size,
      });
    } catch (err: any) {
      dispatch({ type: 'SET_ERROR', error: err.message });
    } finally {
      dispatch({ type: 'SET_LOADING', isLoading: false });
    }
  }, []);

  const selectDashboard = useCallback(async (dashboardId: string, initialFilterValues?: Record<string, any>) => {
    dispatch({ type: 'SET_LOADING', isLoading: true });
    dispatch({ type: 'CLEAR_FILTER_VALUES' });
    try {
      const dashboard = await apiRef.current.getDashboard(dashboardId);

      // Compute effective filter values: defaults first, then URL params override.
      // Hidden filters are excluded — the backend resolves their values from defaults.
      const hiddenParams = new Set(
        dashboard.filters.filter((f) => f.hidden).map((f) => f.parameterName)
      );
      const defaults: Record<string, any> = {};
      dashboard.filters.forEach((f) => {
        if (f.hidden) return;
        if (f.filterType === 'date_range') {
          // Resolve relative expressions like "now-7d" so the picker can
          // consume the seed. Falls back to the current-year range.
          defaults[f.parameterName] = seedDateRangeValue(f.defaultValue);
        } else if (f.defaultValue != null) {
          defaults[f.parameterName] = f.defaultValue;
        }
      });
      const filteredInitialValues: Record<string, any> = {};
      if (initialFilterValues) {
        Object.entries(initialFilterValues).forEach(([key, val]) => {
          if (!hiddenParams.has(key)) {
            filteredInitialValues[key] = val;
          }
        });
      }
      const effectiveFilterValues = { ...defaults, ...filteredInitialValues };

      // Apply filter values before setting loading to false so UI is consistent
      if (Object.keys(effectiveFilterValues).length > 0) {
        dispatch({ type: 'SET_FILTER_VALUES', values: effectiveFilterValues });
      }

      dispatch({ type: 'SET_ACTIVE_DASHBOARD', dashboard });
      dispatch({ type: 'SET_LOADING', isLoading: false });

      // Load filter options (non-blocking for UI)
      if (dashboard.filters.length > 0) {
        try {
          const allOptions = await apiRef.current.getAllFilterOptions(dashboardId, effectiveFilterValues);
          const optionsMap: Record<string, FilterOptionsResult> = {};
          allOptions.forEach((o) => { optionsMap[o.filterId] = o; });
          dispatch({ type: 'SET_FILTER_OPTIONS', options: optionsMap });
        } catch {
          // Filter options are non-critical
        }
      }

      // Execute queries for each widget independently (non-blocking)
      const hasFilters = Object.keys(effectiveFilterValues).length > 0;

      dashboard.widgets.forEach(async (widget) => {
        try {
          const configPageSize = widget.config.pageSize;
          const needsBody = hasFilters || configPageSize != null;
          const data = needsBody
            ? await apiRef.current.executeQueryWithFilters(
                dashboard.id, widget.id, effectiveFilterValues,
                configPageSize ? 1 : undefined, configPageSize,
              )
            : await apiRef.current.executeQuery(dashboard.id, widget.id);
          dispatch({ type: 'SET_WIDGET_DATA', widgetId: widget.id, data });
        } catch {
          dispatch({
            type: 'SET_WIDGET_DATA',
            widgetId: widget.id,
            data: { columns: [], rows: [], error: 'Query execution failed' },
          });
        }
      });
    } catch (err: any) {
      dispatch({ type: 'SET_ERROR', error: err.message });
      dispatch({ type: 'SET_LOADING', isLoading: false });
    }
  }, []);

  const createDashboard = useCallback(
    async (dto: CreateDashboardDTO): Promise<Dashboard> => {
      // Auto-include default hidden global_case_ids filter with SQL query and allow_all.
      const dtoWithFilters: CreateDashboardDTO = {
        ...dto,
        filters: [
          {
            title: 'Global Case IDs',
            parameterName: 'global_case_ids',
            filterType: 'multi_select',
            querySql: 'SELECT id, name FROM narrative WHERE workspace_id = 1 AND id IN (1, 2, 3) ORDER BY id DESC',
            widgetId: null,
            defaultValue: null,
            config: { allow_all: true },
            order: 0,
            hidden: true,
          },
          {
            title: 'Case IDs',
            parameterName: 'case_ids',
            filterType: 'select',
            querySql: 'SELECT id, name FROM narrative WHERE id = ANY({{global_case_ids}})',
            widgetId: null,
            defaultValue: null,
            config: { allow_all: true },
            order: 1,
            hidden: false,
          },
        ],
      };

      const dashboard = await apiRef.current.createDashboard(dtoWithFilters);
      dispatch({ type: 'ADD_DASHBOARD', dashboard });
      dispatch({ type: 'SET_ACTIVE_DASHBOARD', dashboard });
      return dashboard;
    },
    []
  );

  const updateDashboardMeta = useCallback(
    async (dashboardId: string, dto: UpdateDashboardDTO) => {
      const updated = await apiRef.current.updateDashboard(dashboardId, dto);
      dispatch({ type: 'SET_ACTIVE_DASHBOARD', dashboard: updated });
    },
    []
  );

  const deleteDashboard = useCallback(async (dashboardId: string) => {
    await apiRef.current.deleteDashboard(dashboardId);
    dispatch({ type: 'DELETE_DASHBOARD', dashboardId });
  }, []);

  const copyDashboard = useCallback(
    async (
      sourceDashboardId: string,
      newTitle: string,
      targetWorkspaceId?: number,
    ): Promise<Dashboard> => {
      dispatch({ type: 'SET_LOADING', isLoading: true });
      try {
        const fresh = await apiRef.current.copyDashboard(sourceDashboardId, {
          title: newTitle,
          targetWorkspaceId,
        });
        dispatch({ type: 'ADD_DASHBOARD', dashboard: fresh });
        return fresh;
      } catch (err: any) {
        dispatch({ type: 'SET_ERROR', error: err.message });
        throw err;
      } finally {
        dispatch({ type: 'SET_LOADING', isLoading: false });
      }
    },
    []
  );

  // --- Edit mode ---

  const startEditing = useCallback(() => {
    if (!state.activeDashboard) return;
    const dash = state.activeDashboard;
    dispatch({
      type: 'START_EDITING',
      draft: deepClone(dash),
      snapshot: deepClone(dash),
    });

    // Eager-load full SQL for every saved filter. The dashboard GET omits filter
    // SQL, so without this the draft holds placeholder '' until each filter is
    // opened — and draft overrides for un-opened filters would be skipped (or,
    // worse, send empty SQL). Loading it up front means edit-mode queries always
    // resolve filters against their real (or knowingly-edited) SQL, matching the
    // view page. Non-blocking; the flag keeps everything correct until it lands.
    const unloaded = dash.filters.filter((f) => !isTempId(f.id) && !f.querySqlLoaded);
    if (unloaded.length === 0) return;
    Promise.all(
      unloaded.map((f) =>
        apiRef.current.getFilter(dash.id, f.id)
          .then((full) => [f.id, full.querySql] as const)
          .catch(() => null)
      )
    ).then((entries) => {
      const sqlByFilterId: Record<string, string> = {};
      entries.forEach((e) => { if (e) sqlByFilterId[e[0]] = e[1]; });
      if (Object.keys(sqlByFilterId).length > 0) {
        dispatch({ type: 'HYDRATE_FILTER_SQL', sqlByFilterId });
      }
    });
  }, [state.activeDashboard]);

  const cancelEditing = useCallback(() => {
    if (state.hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    dispatch({ type: 'CLEAR_DRAFT' });
  }, [state.hasUnsavedChanges]);

  const saveDashboard = useCallback(async () => {
    const draft = state.draftDashboard;
    const snapshot = state.snapshotDashboard;
    if (!draft || !snapshot) return;

    dispatch({ type: 'SET_SAVING', isSaving: true });

    try {
      const snapshotWidgetIds = new Set(snapshot.widgets.map((w) => w.id));
      const draftWidgetIds = new Set(draft.widgets.map((w) => w.id));

      // 1. Delete removed widgets
      const removedIds = snapshot.widgets
        .filter((w) => !draftWidgetIds.has(w.id))
        .map((w) => w.id);

      for (const id of removedIds) {
        await apiRef.current.removeWidget(id, draft.id);
      }

      // 2. Create new widgets (temp IDs → real IDs)
      const idMap: Record<string, string> = {};
      const newWidgets = draft.widgets.filter((w) => isTempId(w.id));

      for (const widget of newWidgets) {
        const created = await apiRef.current.addWidget({
          dashboardId: draft.id,
          title: widget.title,
          chartType: widget.chartType,
          query: widget.query,
          config: widget.config,
          defaultValues: widget.defaultValues,
        });
        idMap[widget.id] = created.id;
      }

      // 3. Update existing widgets that changed
      const updatedWidgets = draft.widgets.filter(
        (w) => !isTempId(w.id) && snapshotWidgetIds.has(w.id)
      );

      for (const widget of updatedWidgets) {
        const original = snapshot.widgets.find((w) => w.id === widget.id);
        if (!original) continue;

        const changes: UpdateWidgetDTO = {};
        if (widget.title !== original.title) changes.title = widget.title;
        if (widget.chartType !== original.chartType) changes.chartType = widget.chartType;
        if (JSON.stringify(widget.query) !== JSON.stringify(original.query)) changes.query = widget.query;
        if (JSON.stringify(widget.config) !== JSON.stringify(original.config)) changes.config = widget.config;
        if (JSON.stringify(widget.defaultValues) !== JSON.stringify(original.defaultValues)) changes.defaultValues = widget.defaultValues;

        if (Object.keys(changes).length > 0) {
          await apiRef.current.updateWidget(widget.id, draft.id, changes);
        }
      }

      // 4. Save layout (remap temp IDs to real IDs)
      const finalLayout = draft.layout.map((item) => ({
        ...item,
        i: idMap[item.i] || item.i,
      }));

      await apiRef.current.saveLayout(draft.id, finalLayout);

      // 5. Sync filters (widgets are already saved, so remap temp widget IDs)
      const snapshotFilterIds = new Set(snapshot.filters.map((f) => f.id));
      const draftFilterIds = new Set(draft.filters.map((f) => f.id));

      // Delete removed filters
      const removedFilterIds = snapshot.filters
        .filter((f) => !draftFilterIds.has(f.id))
        .map((f) => f.id);
      for (const id of removedFilterIds) {
        await apiRef.current.removeFilter(draft.id, id);
      }

      // Create new filters (temp IDs) — remap widgetId if it was a temp widget
      for (const filter of draft.filters.filter((f) => isTempId(f.id))) {
        const resolvedWidgetId = filter.widgetId && idMap[filter.widgetId]
          ? idMap[filter.widgetId]
          : filter.widgetId;
        await apiRef.current.createFilter(draft.id, {
          title: filter.title,
          parameterName: filter.parameterName,
          filterType: filter.filterType,
          querySql: filter.querySql,
          widgetId: resolvedWidgetId,
          defaultValue: filter.defaultValue,
          config: filter.config,
          order: filter.order,
          hidden: filter.hidden,
        });
      }

      // Update existing filters that changed — remap widgetId if needed.
      // querySqlLoaded is frontend-only bookkeeping (whether the SQL was fetched),
      // so ignore it when deciding whether a filter actually changed.
      const stripLoadedFlag = ({ querySqlLoaded, ...rest }: DashboardFilter) => rest;
      for (const filter of draft.filters.filter((f) => !isTempId(f.id) && snapshotFilterIds.has(f.id))) {
        const original = snapshot.filters.find((f) => f.id === filter.id);
        if (!original || JSON.stringify(stripLoadedFlag(filter)) === JSON.stringify(stripLoadedFlag(original))) continue;
        const resolvedWidgetId = filter.widgetId && idMap[filter.widgetId]
          ? idMap[filter.widgetId]
          : filter.widgetId;
        await apiRef.current.updateFilter(draft.id, filter.id, {
          title: filter.title,
          parameterName: filter.parameterName,
          filterType: filter.filterType,
          // Only persist querySql when the SQL is loaded. An unloaded filter's SQL
          // was never fetched into the frontend (placeholder ''), so sending it
          // would overwrite the real stored SQL with ''. A loaded filter sends its
          // value as-is — including '' when the admin deliberately cleared the SQL.
          querySql: filter.querySqlLoaded ? filter.querySql : undefined,
          widgetId: resolvedWidgetId,
          defaultValue: filter.defaultValue,
          config: filter.config,
          order: filter.order,
          hidden: filter.hidden,
        });
      }

      // 6. Re-fetch the dashboard to get fresh server state
      const fresh = await apiRef.current.getDashboard(draft.id);
      dispatch({ type: 'SET_ACTIVE_DASHBOARD', dashboard: fresh });
      dispatch({ type: 'CLEAR_DRAFT' });
      toast.success(i18n.t('Dashboard saved'));

      // 7. Reapply filter defaults from fresh data (picks up changed default values)
      const freshDefaults: Record<string, any> = {};
      fresh.filters.forEach((f) => {
        if (f.hidden) return;
        if (f.filterType === 'date_range') {
          freshDefaults[f.parameterName] = seedDateRangeValue(f.defaultValue);
        } else if (f.defaultValue != null) {
          freshDefaults[f.parameterName] = f.defaultValue;
        }
      });
      dispatch({ type: 'CLEAR_FILTER_VALUES' });
      if (Object.keys(freshDefaults).length > 0) {
        dispatch({ type: 'SET_FILTER_VALUES', values: freshDefaults });
      }

      // 8. Reload filter options
      const currentFilterValues = freshDefaults;
      if (fresh.filters.length > 0) {
        apiRef.current.getAllFilterOptions(fresh.id, currentFilterValues).then((allOptions) => {
          const optionsMap: Record<string, FilterOptionsResult> = {};
          allOptions.forEach((o) => { optionsMap[o.filterId] = o; });
          dispatch({ type: 'SET_FILTER_OPTIONS', options: optionsMap });
        }).catch(() => {});
      }

      // 9. Execute queries for all widgets in parallel (with pagination & filters)
      const hasFiltersForRefresh = Object.keys(currentFilterValues).length > 0;
      Promise.allSettled(
        fresh.widgets.map(async (widget) => {
          try {
            const configPageSize = widget.config.pageSize;
            const needsBody = hasFiltersForRefresh || configPageSize != null;
            const data = needsBody
              ? await apiRef.current.executeQueryWithFilters(
                  fresh.id, widget.id, currentFilterValues,
                  configPageSize ? 1 : undefined, configPageSize,
                )
              : await apiRef.current.executeQuery(fresh.id, widget.id);
            dispatch({ type: 'SET_WIDGET_DATA', widgetId: widget.id, data });
          } catch {
            dispatch({
              type: 'SET_WIDGET_DATA',
              widgetId: widget.id,
              data: { columns: [], rows: [], error: 'Query execution failed' },
            });
          }
        })
      );
    } catch (err: any) {
      // `state.error` has no consumer in the UI, so surface the failure
      // directly — otherwise a rejected save (e.g. a widget without a title)
      // aborts silently and the draft looks saved.
      dispatch({ type: 'SET_ERROR', error: err.message });
      toast.error(i18n.t('Dashboard could not be saved. Please try again.'));
    } finally {
      dispatch({ type: 'SET_SAVING', isSaving: false });
    }
  }, [state.draftDashboard, state.snapshotDashboard, state.filterValues]);

  // --- Draft operations (local only, no API) ---

  const addWidget = useCallback(
    (dto: CreateWidgetDTO): string => {
      const tempId = generateTempId();

      const widget: Widget = {
        id: tempId,
        dashboardId: dto.dashboardId,
        title: dto.title,
        chartType: dto.chartType,
        query: dto.query || { sql: '' },
        config: dto.config || {},
        defaultValues: dto.defaultValues || {},
        data: null as any, // null triggers the loading spinner in WidgetRenderer
      };

      const currentLayout = state.draftDashboard?.layout || [];
      const maxY = currentLayout.reduce(
        (max, item) => Math.max(max, item.y + item.h),
        0
      );

      const layoutDefaults: Record<string, { w: number; h: number; minW: number; minH: number }> = {
        metric:    { w: 3, h: 2, minW: 2, minH: 2 },
        card_grid: { w: 6, h: 4, minW: 4, minH: 3 },
        text:      { w: 4, h: 3, minW: 1, minH: 1 },
      };
      const dims = layoutDefaults[dto.chartType] ?? { w: 6, h: 4, minW: 3, minH: 2 };
      const layoutItem: DashboardLayoutItem = {
        i: tempId,
        x: 0,
        y: maxY,
        ...dims,
      };

      dispatch({ type: 'DRAFT_ADD_WIDGET', widget, layoutItem });

      // Execute the widget's SQL immediately via raw query so it shows actual data.
      // Include draft hidden filter defaults so unsaved edits are reflected.
      const sql = (dto.query || { sql: '' }).sql?.trim();
      const dashboardId = dto.dashboardId || state.activeDashboard?.id;
      if (sql && dashboardId) {
        const draftFilterValues = mergeFilterValuesWithHiddenDefaults(
          state.filterValues, state.draftDashboard?.filters,
        );
        const draftOverrides = buildDraftFilterOverrides(state.draftDashboard?.filters);
        apiRef.current.executeRawQuery(
          dashboardId, sql, draftFilterValues, undefined, undefined, true, draftOverrides,
        )
          .then((data) => dispatch({ type: 'DRAFT_SET_WIDGET_DATA', widgetId: tempId, data }))
          .catch(() => dispatch({ type: 'DRAFT_SET_WIDGET_DATA', widgetId: tempId, data: { columns: [], rows: [], error: 'Query execution failed' } }));
      }

      return tempId;
    },
    [state.draftDashboard?.layout, state.draftDashboard?.filters, state.activeDashboard?.id, state.filterValues]
  );

  const updateWidget = useCallback(
    (widgetId: string, dto: UpdateWidgetDTO) => {
      dispatch({ type: 'DRAFT_UPDATE_WIDGET', widgetId, updates: dto });
    },
    []
  );

  const removeWidget = useCallback((widgetId: string) => {
    dispatch({ type: 'DRAFT_REMOVE_WIDGET', widgetId });
  }, []);

  const copyWidget = useCallback(
    async (widgetId: string) => {
      const draft = state.draftDashboard;
      if (!draft) return;

      const source = draft.widgets.find((w) => w.id === widgetId);
      if (!source) return;

      // Fetch full widget details (incl. SQL) for saved widgets
      const detailed = isTempId(widgetId)
        ? source
        : await apiRef.current.getWidget(draft.id, widgetId);

      const tempId = generateTempId();

      const widget: Widget = {
        ...deepClone(detailed),
        id: tempId,
        dashboardId: draft.id,
        title: `${detailed.title} (copy)`,
      };

      const sourceLayout = draft.layout.find((l) => l.i === widgetId);
      const currentLayout = draft.layout;
      const maxY = currentLayout.reduce(
        (max, item) => Math.max(max, item.y + item.h),
        0
      );

      const layoutItem: DashboardLayoutItem = {
        i: tempId,
        x: sourceLayout?.x ?? 0,
        y: maxY,
        w: sourceLayout?.w ?? 6,
        h: sourceLayout?.h ?? 4,
        minW: sourceLayout?.minW ?? 3,
        minH: sourceLayout?.minH ?? 2,
      };

      dispatch({ type: 'DRAFT_ADD_WIDGET', widget, layoutItem });
    },
    [state.draftDashboard]
  );

  const executeWidgetQuery = useCallback(
    async (widgetId: string, page?: number, pageSize?: number) => {
      const dashboardId = state.activeDashboard?.id;
      if (!dashboardId) return;

      const target = state.isEditing ? 'DRAFT_SET_WIDGET_DATA' : 'SET_WIDGET_DATA';
      dispatch({ type: target, widgetId, data: null as any });

      // In edit mode, always run the draft SQL via raw query (saved SQL may be stale).
      // Hidden filter defaults edited locally aren't persisted yet — send them explicitly
      // so the preview reflects unsaved draft values.
      if (state.isEditing) {
        const widgets = state.draftDashboard?.widgets ?? [];
        const widget = widgets.find((w) => w.id === widgetId);
        const sql = widget?.query?.sql?.trim();
        if (!sql) return;
        const configPageSize = widget?.config?.pageSize;
        const effectivePage = page ?? (configPageSize ? 1 : undefined);
        const effectivePageSize = pageSize ?? configPageSize;
        const draftFilterValues = mergeFilterValuesWithHiddenDefaults(
          state.filterValues, state.draftDashboard?.filters,
        );
        const draftOverrides = buildDraftFilterOverrides(state.draftDashboard?.filters);
        const data = await apiRef.current.executeRawQuery(
          dashboardId, sql, draftFilterValues, effectivePage, effectivePageSize, true, draftOverrides,
        );
        dispatch({ type: target, widgetId, data });
        return;
      }

      const hasFilters = Object.keys(state.filterValues).length > 0;
      const hasPagination = page != null && pageSize != null;
      const data: WidgetDataResult = (hasFilters || hasPagination)
        ? await apiRef.current.executeQueryWithFilters(dashboardId, widgetId, state.filterValues, page, pageSize)
        : await apiRef.current.executeQuery(dashboardId, widgetId);
      dispatch({ type: target, widgetId, data });
      if (data.error) {
        console.warn(`Widget query error (widget=${widgetId}):`, data.error);
      }
    },
    [state.activeDashboard?.id, state.isEditing, state.filterValues, state.draftDashboard?.widgets, state.draftDashboard?.filters]
  );

  const invalidateWidgetCache = useCallback(
    async (widgetId: string) => {
      const dashboardId = state.activeDashboard?.id;
      if (!dashboardId) return;
      await apiRef.current.invalidateWidgetCache(dashboardId, widgetId);
    },
    [state.activeDashboard?.id]
  );

  const updateLayout = useCallback((layout: DashboardLayoutItem[]) => {
    dispatch({ type: 'DRAFT_UPDATE_LAYOUT', layout });
  }, []);

  const selectWidget = useCallback((widgetId: string | null) => {
    dispatch({ type: 'SELECT_WIDGET', widgetId });
  }, []);

  const setFilterValue = useCallback((parameterName: string, value: any) => {
    dispatch({ type: 'SET_FILTER_VALUE', parameterName, value });

    if (!state.activeDashboard) return;

    const dashboardId = state.activeDashboard.id;
    const target = state.isEditing ? 'DRAFT_SET_WIDGET_DATA' : 'SET_WIDGET_DATA';
    const widgets = state.isEditing ? (state.draftDashboard?.widgets ?? []) : state.activeDashboard.widgets;
    const filters = state.isEditing ? (state.draftDashboard?.filters ?? []) : state.activeDashboard.filters;

    // Find filter definition to determine scope
    const filterDef = filters.find((f) => f.parameterName === parameterName);
    const affectedWidgets = filterDef?.widgetId
      ? widgets.filter((w) => w.id === filterDef.widgetId)
      : widgets;

    const newFilterValues = { ...state.filterValues, [parameterName]: value };
    if (value === undefined || value === null) delete newFilterValues[parameterName];

    // Re-execute affected widget queries
    affectedWidgets.forEach((widget) => {
      dispatch({ type: target, widgetId: widget.id, data: null as any });

      // In edit mode, always run draft SQL via raw query (saved SQL may be stale)
      if (state.isEditing) {
        const sql = widget.query?.sql?.trim();
        if (!sql) return;
        const ps = widget.config?.pageSize;
        const draftFilterValues = mergeFilterValuesWithHiddenDefaults(
          newFilterValues, state.draftDashboard?.filters,
        );
        const draftOverrides = buildDraftFilterOverrides(state.draftDashboard?.filters);
        apiRef.current.executeRawQuery(
          dashboardId, sql, draftFilterValues, ps ? 1 : undefined, ps, true, draftOverrides,
        )
          .then((data) => dispatch({ type: target, widgetId: widget.id, data }))
          .catch(() => dispatch({ type: target, widgetId: widget.id, data: { columns: [], rows: [], error: 'Query execution failed' } }));
        return;
      }

      const configPageSize = widget.config.pageSize;
      const hasFilters = Object.keys(newFilterValues).length > 0;
      const hasPagination = configPageSize != null;

      const promise = (hasFilters || hasPagination)
        ? apiRef.current.executeQueryWithFilters(dashboardId, widget.id, newFilterValues, configPageSize ? 1 : undefined, configPageSize)
        : apiRef.current.executeQuery(dashboardId, widget.id);
      promise
        .then((data) => dispatch({ type: target, widgetId: widget.id, data }))
        .catch(() => dispatch({ type: target, widgetId: widget.id, data: { columns: [], rows: [], error: 'Query execution failed' } }));
    });

    // Reload filter options with new filter values (cascading/dependent filters).
    // In edit mode, merge draft hidden defaults so dependent filter SQL sees unsaved values
    // and send draft filter overrides so unsaved filter SQL takes effect server-side.
    const optionsFilterValues = state.isEditing
      ? mergeFilterValuesWithHiddenDefaults(newFilterValues, state.draftDashboard?.filters)
      : newFilterValues;
    const optionsDraftOverrides = state.isEditing
      ? buildDraftFilterOverrides(state.draftDashboard?.filters)
      : undefined;
    apiRef.current.getAllFilterOptions(
      dashboardId, optionsFilterValues, state.isEditing, optionsDraftOverrides,
    ).then((allOptions) => {
      const optionsMap: Record<string, FilterOptionsResult> = {};
      allOptions.forEach((o) => { optionsMap[o.filterId] = o; });
      dispatch({ type: 'SET_FILTER_OPTIONS', options: optionsMap });
    }).catch(() => {});
  }, [state.isEditing, state.activeDashboard, state.draftDashboard, state.filterValues]);

  const setFilterValues = useCallback((values: Record<string, any>) => {
    dispatch({ type: 'SET_FILTER_VALUES', values });
  }, []);

  const clearFilterValues = useCallback(() => {
    // Reset to defaults instead of clearing completely
    const filters = state.isEditing
      ? (state.draftDashboard?.filters ?? [])
      : (state.activeDashboard?.filters ?? []);
    const yr = new Date().getFullYear();
    const defaults: Record<string, any> = {};
    filters.forEach((f) => {
      if (f.hidden) return;
      if (f.defaultValue != null) {
        defaults[f.parameterName] = f.defaultValue;
      } else if (f.filterType === 'date_range') {
        defaults[f.parameterName] = { from: `${yr}-01-01`, to: `${yr}-12-31` };
      } else if (f.filterType === 'text') {
        // text filters reset to empty (backend sends % which matches everything)
      } else if (f.config?.allow_all) {
        defaults[f.parameterName] = '__all__';
      }
    });
    dispatch({ type: 'CLEAR_FILTER_VALUES' });
    if (Object.keys(defaults).length > 0) {
      dispatch({ type: 'SET_FILTER_VALUES', values: defaults });
    }

    // Re-execute all widget queries with reset filter values
    const dashboardId = state.activeDashboard?.id;
    if (!dashboardId) return;
    const target = state.isEditing ? 'DRAFT_SET_WIDGET_DATA' : 'SET_WIDGET_DATA';
    const widgets = state.isEditing
      ? (state.draftDashboard?.widgets ?? [])
      : (state.activeDashboard?.widgets ?? []);

    widgets.forEach((widget) => {
      dispatch({ type: target, widgetId: widget.id, data: null as any });

      // In edit mode, always run draft SQL via raw query (saved SQL may be stale)
      if (state.isEditing) {
        const sql = widget.query?.sql?.trim();
        if (!sql) return;
        const ps = widget.config?.pageSize;
        const draftFilterValues = mergeFilterValuesWithHiddenDefaults(
          defaults, state.draftDashboard?.filters,
        );
        const draftOverrides = buildDraftFilterOverrides(state.draftDashboard?.filters);
        apiRef.current.executeRawQuery(
          dashboardId, sql, draftFilterValues, ps ? 1 : undefined, ps, true, draftOverrides,
        )
          .then((data) => dispatch({ type: target, widgetId: widget.id, data }))
          .catch(() => dispatch({ type: target, widgetId: widget.id, data: { columns: [], rows: [], error: 'Query execution failed' } }));
        return;
      }

      const configPageSize = widget.config.pageSize;
      const hasFilters = Object.keys(defaults).length > 0;
      const hasPagination = configPageSize != null;

      const promise = (hasFilters || hasPagination)
        ? apiRef.current.executeQueryWithFilters(dashboardId, widget.id, defaults, configPageSize ? 1 : undefined, configPageSize)
        : apiRef.current.executeQuery(dashboardId, widget.id);
      promise
        .then((data) => dispatch({ type: target, widgetId: widget.id, data }))
        .catch(() => dispatch({ type: target, widgetId: widget.id, data: { columns: [], rows: [], error: 'Query execution failed' } }));
    });

    // Reload filter options with reset values; in edit mode, send draft hidden defaults
    // so cascading/dependent filter SQL sees unsaved hidden values, plus draft filter
    // overrides so unsaved filter SQL takes effect server-side.
    const optionsFilterValues = state.isEditing
      ? mergeFilterValuesWithHiddenDefaults(defaults, state.draftDashboard?.filters)
      : defaults;
    const optionsDraftOverrides = state.isEditing
      ? buildDraftFilterOverrides(state.draftDashboard?.filters)
      : undefined;
    apiRef.current.getAllFilterOptions(
      dashboardId, optionsFilterValues, state.isEditing, optionsDraftOverrides,
    ).then((allOptions) => {
      const optionsMap: Record<string, FilterOptionsResult> = {};
      allOptions.forEach((o) => { optionsMap[o.filterId] = o; });
      dispatch({ type: 'SET_FILTER_OPTIONS', options: optionsMap });
    }).catch(() => {});
  }, [state.isEditing, state.draftDashboard, state.activeDashboard]);

  const loadFilterOptions = useCallback(async () => {
    const dashboardId = state.activeDashboard?.id;
    if (!dashboardId) return;
    try {
      // In edit mode, merge draft hidden defaults so cascading filter SQL sees unsaved values
      // and send draft filter overrides so unsaved filter SQL is honored server-side.
      const optionsFilterValues = state.isEditing
        ? mergeFilterValuesWithHiddenDefaults(state.filterValues, state.draftDashboard?.filters)
        : state.filterValues;
      const optionsDraftOverrides = state.isEditing
        ? buildDraftFilterOverrides(state.draftDashboard?.filters)
        : undefined;
      const allOptions = await apiRef.current.getAllFilterOptions(
        dashboardId, optionsFilterValues, state.isEditing, optionsDraftOverrides,
      );
      const optionsMap: Record<string, FilterOptionsResult> = {};
      allOptions.forEach((o) => { optionsMap[o.filterId] = o; });
      dispatch({ type: 'SET_FILTER_OPTIONS', options: optionsMap });
    } catch {
      // non-critical
    }
  }, [state.activeDashboard?.id, state.isEditing, state.filterValues, state.draftDashboard?.filters]);

  const saveDraftFilters = useCallback(async () => {
    const draft = state.draftDashboard;
    const dashboardId = draft?.id ?? state.activeDashboard?.id;
    if (!dashboardId) return;

    const filters = draft?.filters ?? state.activeDashboard?.filters ?? [];
    const unsavedFilters = filters.filter((f) => isTempId(f.id));
    if (unsavedFilters.length === 0) return;

    for (const filter of unsavedFilters) {
      const created = await apiRef.current.createFilter(dashboardId, {
        title: filter.title,
        parameterName: filter.parameterName,
        filterType: filter.filterType,
        querySql: filter.querySql,
        widgetId: filter.widgetId,
        defaultValue: filter.defaultValue,
        config: filter.config,
        order: filter.order,
        hidden: filter.hidden,
      });
      // Replace temp filter with saved version in draft
      dispatch({ type: 'DRAFT_UPDATE_FILTER', filterId: filter.id, updates: { id: created.id } });
    }
  }, [state.draftDashboard, state.activeDashboard]);

  const reloadEditingDashboard = useCallback(async () => {
    const dashboardId = state.activeDashboard?.id;
    if (!dashboardId) return;
    const fresh = await apiRef.current.getDashboard(dashboardId);
    dispatch({ type: 'SET_ACTIVE_DASHBOARD', dashboard: fresh });
    if (state.isEditing) {
      // Merge: keep draft widgets/layout but update filters from server
      const currentDraft = state.draftDashboard;
      if (currentDraft) {
        dispatch({
          type: 'START_EDITING',
          draft: { ...currentDraft, filters: fresh.filters },
          snapshot: fresh,
        });
      }
    }
    // Apply defaults for any new filters that don't have values yet
    const yr = new Date().getFullYear();
    const newDefaults: Record<string, any> = {};
    fresh.filters.forEach((f) => {
      if (f.hidden) return;
      if (state.filterValues[f.parameterName] != null) return; // already has a value
      if (f.defaultValue != null) {
        newDefaults[f.parameterName] = f.defaultValue;
      } else if (f.filterType === 'date_range') {
        newDefaults[f.parameterName] = { from: `${yr}-01-01`, to: `${yr}-12-31` };
      }
    });
    if (Object.keys(newDefaults).length > 0) {
      dispatch({ type: 'SET_FILTER_VALUES', values: newDefaults });
    }
  }, [state.activeDashboard?.id, state.isEditing, state.draftDashboard, state.filterValues]);

  const addFilter = useCallback((dto: CreateFilterDTO) => {
    const filter: DashboardFilter = {
      id: generateTempId(),
      dashboardId: state.draftDashboard?.id ?? '',
      widgetId: dto.widgetId ?? null,
      title: dto.title,
      parameterName: dto.parameterName,
      filterType: dto.filterType,
      querySql: dto.querySql,
      querySqlLoaded: true, // locally created — its SQL is known
      defaultValue: dto.defaultValue ?? null,
      config: dto.config ?? {},
      order: dto.order ?? 0,
      hidden: dto.hidden ?? false,
    };
    dispatch({ type: 'DRAFT_ADD_FILTER', filter });
    // Seed the filter value for new visible date_range filters so the picker
    // renders immediately with the resolved default (including relatives).
    if (!filter.hidden && filter.filterType === 'date_range' && filter.parameterName) {
      dispatch({
        type: 'SET_FILTER_VALUE',
        parameterName: filter.parameterName,
        value: seedDateRangeValue(filter.defaultValue),
      });
    }
  }, [state.draftDashboard?.id]);

  const updateFilter = useCallback((filterId: string, updates: Partial<DashboardFilter>) => {
    dispatch({ type: 'DRAFT_UPDATE_FILTER', filterId, updates });
    // If the user changed a date_range filter's default_value in the editor,
    // re-seed the current filter value so the picker (and any widgets keyed
    // on this filter) reflect the new relative expression immediately —
    // without waiting for a dashboard save + reload.
    if (!('defaultValue' in updates)) return;
    const existing = state.draftDashboard?.filters.find((f) => f.id === filterId);
    if (!existing) return;
    const filterType = updates.filterType ?? existing.filterType;
    const parameterName = updates.parameterName ?? existing.parameterName;
    const hidden = updates.hidden ?? existing.hidden;
    if (hidden || filterType !== 'date_range' || !parameterName) return;
    dispatch({
      type: 'SET_FILTER_VALUE',
      parameterName,
      value: seedDateRangeValue(updates.defaultValue),
    });
  }, [state.draftDashboard]);

  const removeFilter = useCallback((filterId: string) => {
    dispatch({ type: 'DRAFT_REMOVE_FILTER', filterId });
  }, []);

  const displayedDashboard = state.isEditing
    ? state.draftDashboard
    : state.activeDashboard;

  const value: DashboardContextValue = {
    ...state,
    isAdmin,
    displayedDashboard,
    fetchDashboards,
    selectDashboard,
    createDashboard,
    updateDashboardMeta,
    deleteDashboard,
    copyDashboard,
    startEditing,
    saveDashboard,
    cancelEditing,
    addWidget,
    updateWidget,
    removeWidget,
    copyWidget,
    executeWidgetQuery,
    invalidateWidgetCache,
    updateLayout,
    selectWidget,
    setFilterValue,
    setFilterValues,
    clearFilterValues,
    loadFilterOptions,
    saveDraftFilters,
    reloadEditingDashboard,
    addFilter,
    updateFilter,
    removeFilter,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};
